/**
 * The Markdown subset the two legal documents are written in — parsed, never
 * rewritten.
 *
 * ── Why a parser and not a paste ────────────────────────────────────────────
 *
 * `apps/web/src/content/legal/*.md` are **post-attorney-review finals**, copied
 * byte-for-byte from `techdad-company/projects/wordy-royale/legal/`
 * (`tos-new.md`, `privacy-policy-new.md`), which stays the canonical origin. A
 * changed word in there is a legal problem, not a copy edit — so the documents
 * are shipped as their own source text and turned into elements at runtime.
 * Nobody retypes them into JSX, nobody "tidies" a sentence to fit a line, and
 * the next revision is a file copy plus a hash check.
 *
 * ── Why a hand-written one, and why it is this small ────────────────────────
 *
 * Pulling in a Markdown library would add a dependency (and, for anything that
 * emits HTML, a `dangerouslySetInnerHTML` on a document we did not author line
 * by line) to render **six constructs**. What the two documents actually use is
 * enumerated below and nothing else; `legal-doc.test.ts` asserts that, so a
 * future revision that introduces a seventh fails CI rather than quietly losing
 * a clause.
 *
 * Blocks:  `#` / `##` headings · paragraphs · `-` bullet lists · pipe tables
 * Inline:  `**strong**` · `` `code` `` · `[label](href)`
 *
 * **The parser is total: every non-blank line ends up inside a block.** An
 * unrecognised line becomes a paragraph carrying its own raw text rather than
 * being dropped — losing a sentence silently is the one failure mode this file
 * exists to prevent, and a stray marker on screen is a bug report where a
 * missing clause is not. `unsupportedLines()` is the strict half of that pair
 * and is asserted empty in the test.
 */

export type Inline =
  | { kind: "text"; value: string }
  | { kind: "strong"; value: string }
  | { kind: "code"; value: string }
  | { kind: "link"; value: string; href: string };

export type Block =
  | { kind: "heading"; level: 1 | 2; content: Inline[] }
  | { kind: "paragraph"; content: Inline[] }
  | { kind: "list"; items: Inline[][] }
  | { kind: "table"; head: Inline[][]; rows: Inline[][][] };

const HEADING = /^(#{1,2})\s+(.*)$/;
const BULLET = /^-\s+(.*)$/;
const TABLE_ROW = /^\|/;
/** `|---|`, `| :--- |`, `| ---: |` — the row that declares column alignment. */
const TABLE_RULE = /^\|(\s*:?-{2,}:?\s*\|)+$/;

/**
 * One regex, one pass, so the three inline forms cannot fight over the same
 * characters. Order matters only inside the alternation: code spans are matched
 * before anything can look inside them.
 */
const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;

export function parseInline(source: string): Inline[] {
  const out: Inline[] = [];

  for (const part of source.split(INLINE)) {
    if (part === "") continue;

    if (part.startsWith("`") && part.endsWith("`")) {
      out.push({ kind: "code", value: part.slice(1, -1) });
      continue;
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      out.push({ kind: "strong", value: part.slice(2, -2) });
      continue;
    }

    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link?.[1] !== undefined && link[2] !== undefined) {
      out.push({ kind: "link", value: link[1], href: link[2] });
      continue;
    }

    out.push({ kind: "text", value: part });
  }

  return out;
}

/** `| a | b |` → `["a", "b"]`. The outer pipes are delimiters, not cells. */
function tableCells(line: string): string[] {
  return line
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

export function parseLegalDocument(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? "";

    if (line.trim() === "") {
      index += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading?.[1] !== undefined && heading[2] !== undefined) {
      blocks.push({
        kind: "heading",
        level: heading[1].length === 1 ? 1 : 2,
        content: parseInline(heading[2]),
      });
      index += 1;
      continue;
    }

    if (BULLET.test(line)) {
      const items: Inline[][] = [];
      while (index < lines.length) {
        const item = BULLET.exec(lines[index] ?? "");
        if (item?.[1] === undefined) break;
        items.push(parseInline(item[1]));
        index += 1;
      }
      blocks.push({ kind: "list", items });
      continue;
    }

    if (TABLE_ROW.test(line)) {
      const rows: Inline[][][] = [];
      let head: Inline[][] = [];
      let first = true;

      while (index < lines.length) {
        const row = lines[index] ?? "";
        if (!TABLE_ROW.test(row)) break;
        index += 1;

        // The alignment rule carries no content; it is punctuation about the
        // table, and rendering it would put `---` on screen.
        if (TABLE_RULE.test(row)) continue;

        const cells = tableCells(row).map(parseInline);
        if (first) {
          head = cells;
          first = false;
        } else {
          rows.push(cells);
        }
      }

      blocks.push({ kind: "table", head, rows });
      continue;
    }

    // A paragraph runs until a blank line or the start of another block —
    // Markdown's soft-wrap rule, so a document that wraps its prose at 80
    // columns renders as one paragraph rather than as N stacked ones.
    const paragraph: string[] = [];
    while (index < lines.length) {
      const next = lines[index] ?? "";
      if (next.trim() === "" || HEADING.test(next) || BULLET.test(next) || TABLE_ROW.test(next)) {
        break;
      }
      paragraph.push(next.trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", content: parseInline(paragraph.join(" ")) });
  }

  return blocks;
}

/**
 * Every line the parser above would treat as prose despite it carrying Markdown
 * that means something else — block quotes, `###`+ headings, ordered lists,
 * images, horizontal rules, fenced code.
 *
 * Not used at runtime: it is the assertion in `legal-doc.test.ts`, and it is
 * how "the documents only use the six constructs" stays true of the *next*
 * revision rather than only of this one.
 */
export function unsupportedLines(source: string): string[] {
  const unsupported = /^(?:#{3,}\s|>|\d+\.\s|!\[|```|\s{4,}\S|(?:---|\*\*\*|___)\s*$)/;
  return source
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line) => unsupported.test(line));
}
